import {
  ArrowsClockwiseIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
} from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";

import { useAccountCurrency } from "@/client/services/currency/currency";

import type { PaginatedWithdrawal } from "../types/wallet";

interface WithdrawalsHistoryProps {
  data: PaginatedWithdrawal | undefined;
  loading: boolean;
  page: number;
  onPageChange: (page: number) => void;
}

const formatDate = (value: string | Date) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Date inconnue";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

export const WithdrawalsHistory = ({
  data,
  loading,
  page,
  onPageChange,
}: WithdrawalsHistoryProps) => {
  const { format } = useAccountCurrency();

  const withdrawals = data?.data ?? [];

  /*
   * Le backend renvoie dans `page`
   * le nombre total de pages.
   */
  const totalPages = Math.max(data?.page ?? 1, 1);

  return (
    <article className="overflow-hidden rounded-2xl border border-indigo-100 bg-white shadow-[0_10px_30px_rgba(30,27,75,0.06)]">
      <header className="flex items-start gap-3 border-b border-indigo-50 px-4 py-4 sm:items-center sm:px-6">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-700">
          <ArrowsClockwiseIcon size={20} weight="duotone" />
        </div>

        <div className="min-w-0">
          <h2 className="text-sm font-bold text-[#071451] sm:text-base">Historique des retraits</h2>

          <p className="mt-0.5 text-xs leading-5 text-slate-500 sm:text-sm">
            Suivez l’état de vos demandes de transfert.
          </p>
        </div>
      </header>

      {loading && !data ? (
        <div className="flex flex-col gap-3 p-4 sm:p-6" aria-label="Chargement des retraits">
          {[0, 1, 2].map((item) => (
            <div key={item} className="h-24 animate-pulse rounded-xl bg-slate-100 sm:h-20" />
          ))}
        </div>
      ) : withdrawals.length === 0 ? (
        <div className="flex min-h-56 flex-col items-center justify-center p-6 text-center">
          <span className="grid size-12 place-items-center rounded-2xl bg-indigo-50 text-indigo-300">
            <ArrowsClockwiseIcon size={26} weight="duotone" />
          </span>

          <h3 className="mt-4 text-sm font-bold text-[#071451]">Aucun retrait</h3>

          <p className="mt-1 max-w-xs text-xs leading-5 text-slate-500 sm:text-sm">
            Vos prochaines demandes de retrait apparaîtront ici avec leur état de traitement.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {withdrawals.map((item) => {
            const isAccepted = item.status === "ACCEPTED";

            const isPending = item.status === "PENDING";

            const isRejected = item.status === "REJECTED";

            const statusLabel = isAccepted ? "Effectué" : isPending ? "En attente" : "Rejeté";

            return (
              <div
                key={item.id}
                className={cn(
                  "grid gap-3 px-4 py-4 transition-colors hover:bg-indigo-50/30",
                  "sm:grid-cols-[auto_minmax(0,1fr)_auto]",
                  "sm:items-center sm:gap-4 sm:px-6",
                )}
              >
                <div className="flex min-w-0 items-start gap-3 sm:contents">
                  <div
                    className={cn(
                      "flex size-10 shrink-0 items-center justify-center rounded-full",
                      isAccepted && "bg-emerald-50 text-emerald-600",
                      isPending && "bg-amber-50 text-amber-600",
                      isRejected && "bg-rose-50 text-rose-600",
                    )}
                  >
                    {isAccepted && <CheckCircleIcon size={20} weight="fill" />}

                    {isPending && <ClockIcon size={20} weight="fill" />}

                    {isRejected && <XCircleIcon size={20} weight="fill" />}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="min-w-0 truncate text-sm font-bold text-[#071451]">
                        Retrait vers {item.mean === "MOBILE_MONEY" ? "Mobile Money" : "Banque"}
                      </p>

                      <span
                        className={cn(
                          "inline-flex rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide sm:hidden",
                          isAccepted && "bg-emerald-50 text-emerald-700",
                          isPending && "bg-amber-50 text-amber-700",
                          isRejected && "bg-rose-50 text-rose-700",
                        )}
                      >
                        {statusLabel}
                      </span>
                    </div>

                    <div className="mt-1 flex flex-col gap-0.5 text-[10px] text-slate-500 min-[420px]:flex-row min-[420px]:items-center min-[420px]:gap-2 sm:text-xs">
                      <span>{formatDate(item.demandDate)}</span>

                      <span aria-hidden className="hidden min-[420px]:inline">
                        •
                      </span>

                      <span className="truncate font-mono text-[9px] uppercase text-slate-400 sm:text-[10px]">
                        Réf. {item.reference.split("-")[0]}
                      </span>
                    </div>

                    {isRejected && item.reason && (
                      <p className="mt-2 rounded-lg bg-rose-50 px-2.5 py-2 text-[10px] leading-4 text-rose-600 sm:text-xs">
                        Motif : {item.reason}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between border-t border-slate-100 pt-3 sm:block sm:border-0 sm:pt-0 sm:text-right">
                  <p
                    className={cn(
                      "text-sm font-bold tabular-nums",
                      isAccepted && "text-emerald-700",
                      isPending && "text-amber-700",
                      isRejected && "text-rose-700 line-through opacity-70",
                    )}
                  >
                    -{format(item.amount)}
                  </p>

                  <p
                    className={cn(
                      "hidden text-[10px] font-bold uppercase tracking-wider sm:mt-1 sm:block",
                      isAccepted && "text-emerald-600",
                      isPending && "text-amber-600",
                      isRejected && "text-rose-600",
                    )}
                  >
                    {statusLabel}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <footer className="flex items-center justify-between gap-2 border-t border-indigo-50 px-3 py-3 sm:px-6">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            aria-label="Afficher la page précédente"
            className={cn(
              "inline-flex min-h-10 items-center justify-center gap-1.5 rounded-xl border border-indigo-100 bg-white px-3 text-xs font-bold text-indigo-700 transition",
              "hover:border-indigo-300 hover:bg-indigo-50",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500",
              "disabled:cursor-not-allowed disabled:opacity-40",
            )}
          >
            <CaretLeftIcon size={15} weight="bold" />

            <span className="hidden min-[390px]:inline">Précédent</span>
          </button>

          <span
            className="shrink-0 rounded-full bg-indigo-50 px-3 py-1.5 text-[10px] font-bold text-indigo-700 sm:text-xs"
            aria-live="polite"
          >
            Page {page} sur {totalPages}
          </span>

          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            aria-label="Afficher la page suivante"
            className={cn(
              "inline-flex min-h-10 items-center justify-center gap-1.5 rounded-xl border border-indigo-100 bg-white px-3 text-xs font-bold text-indigo-700 transition",
              "hover:border-indigo-300 hover:bg-indigo-50",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500",
              "disabled:cursor-not-allowed disabled:opacity-40",
            )}
          >
            <span className="hidden min-[390px]:inline">Suivant</span>

            <CaretRightIcon size={15} weight="bold" />
          </button>
        </footer>
      )}
    </article>
  );
};
